import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, BookOpen } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import LatestFromBlog from '../../components/v2/LatestFromBlog';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';
import { PILLARS } from './pillars.data';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Pillar (topic hub) page. Renders one hub per pillar slug from pillars.data.
 * The hub concentrates topical signal for one keyword theme, links out to
 * the associated blog posts, and drives a single Book-a-Call CTA.
 *
 * Google reads dense internal linking under a pillar as strong topical
 * authority, this is the "compounding" lever behind good SEO. Every new
 * spoke post the auto-publisher writes should mention the pillar phrase so
 * it gets auto-linked back here on the next interlink pass.
 */
export default function Pillar({ pillarSlug: pillarSlugProp }) {
  const params = useParams();
  const slug = pillarSlugProp || params.slug;
  const pillar = PILLARS.find((p) => p.slug === slug);

  const [spokes, setSpokes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pillar) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog?published_only=true`);
        const all = res.data || [];
        // Match posts whose title or excerpt contains any of the pillar's spoke keywords.
        const matcher = new RegExp(pillar.spokeKeywords.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'i');
        const matched = all.filter((p) => matcher.test((p.title || '') + ' ' + (p.excerpt || '')));
        if (!cancelled) setSpokes(matched.slice(0, 15));
      } catch (_) {
        if (!cancelled) setSpokes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pillar]);

  if (!pillar) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center font-body text-[#475569]">
        <div className="text-center">
          <h1 className="font-display font-bold text-[#1E2A44] text-3xl">Guide not found</h1>
          <Link to="/blog" className="text-[#BC516F] mt-4 inline-block">Back to the blog →</Link>
        </div>
      </main>
    );
  }

  // Top-level pillars (podcast-launching, book-publishing) live at /:slug.
  // Legacy pillars live under /guides/:slug. Both are canonicalised here so
  // schema, SEO url, and breadcrumb links stay consistent.
  const pillarPath = pillarSlugProp ? `/${pillar.slug}` : `/guides/${pillar.slug}`;
  const pillarUrl = `https://audioandco.com${pillarPath}`;
  const faqs = Array.isArray(pillar.faq) ? pillar.faq.filter((f) => f && f.q && f.a) : [];

  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title={pillar.metaTitle}
        description={pillar.metaDescription}
        keywords={pillar.keywords}
        url={pillarUrl}
      />

      {/* Breadcrumb schema */}
      <script
        type="application/ld+json"
        data-schema="breadcrumb"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://audioandco.com/' },
              { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://audioandco.com/blog' },
              { '@type': 'ListItem', position: 3, name: pillar.shortTitle, item: pillarUrl },
            ],
          }),
        }}
      />

      {/* FAQPage schema (only when at least 2 FAQs defined) */}
      {faqs.length >= 2 && (
        <script
          type="application/ld+json"
          data-schema="faq"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }),
          }}
        />
      )}

      <V2Hero imageUrl={pillar.heroImage} alt={pillar.heroAlt} title={`${pillar.title.replace(/[.]$/, '')} | Audio & Co.`} testId={`pillar-${pillar.slug}-hero`}>
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Guide</p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            {pillar.title}
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">{pillar.subheading}</p>
          <div className="mt-8">
            <CTABlock size="lg" testIdPrefix={`pillar-${pillar.slug}-cta`} />
          </div>
        </div>
      </V2Hero>

      {/* Overview */}
      <Section bg="white" testId={`pillar-${pillar.slug}-intro`}>
        <div className="max-w-3xl">
          {pillar.intro.map((para, i) => (
            <p key={i} className="text-[17px] leading-relaxed text-[#475569] mb-5">{para}</p>
          ))}
        </div>
      </Section>

      {/* Sections */}
      <Section bg="bg" testId={`pillar-${pillar.slug}-sections`}>
        <div className="max-w-3xl">
          {pillar.sections.map((s, i) => (
            <div key={i} className="mb-10 last:mb-0" data-speakable>
              <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl tracking-tight">{s.heading}</h2>
              <p className="text-[17px] leading-relaxed text-[#475569] mt-3">{s.body}</p>
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#1E2A44]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#BC516F] mt-2.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Related blog posts (spokes) */}
      <Section bg="white" testId={`pillar-${pillar.slug}-spokes`}>
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Deep dives on this topic</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Read the whole library.</h2>
          <p className="text-[#475569] mt-3">Every post below explores one facet of {pillar.shortTitle}. Read the ones that fit your situation.</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && <p className="text-[#64748B]">Loading related posts…</p>}
          {!loading && spokes.length === 0 && (
            <p className="text-[#64748B]">No matching posts yet. <Link to="/blog" className="text-[#BC516F]">Browse the blog</Link>.</p>
          )}
          {spokes.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="block bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-5 hover:shadow-[0_10px_30px_rgba(30,42,68,0.08)] transition"
              data-testid={`pillar-spoke-${p.slug}`}
            >
              <BookOpen className="w-4 h-4 text-[#BC516F]" />
              <h3 className="font-display font-semibold text-[#1E2A44] text-[17px] mt-3 leading-snug">{p.title}</h3>
              {p.excerpt && <p className="text-[#475569] text-sm mt-2 leading-relaxed line-clamp-3">{p.excerpt}</p>}
              <span className="text-[#BC516F] text-sm font-semibold mt-3 inline-block">Read the guide →</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Latest from blog (mixed, drives cross-navigation) */}
      <LatestFromBlog
        eyebrow="Fresh from the blog"
        heading="What we're writing this week"
        subheading="Daily posts on visibility, podcasts and content for small business owners."
        bg="bg"
        testId={`pillar-${pillar.slug}-latest`}
      />

      {/* FAQ */}
      {faqs.length >= 2 && (
        <Section bg="white" testId={`pillar-${pillar.slug}-faq`}>
          <div className="max-w-3xl">
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Frequently asked</p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Straight answers.</h2>
            <p className="text-[#475569] mt-3">The questions we get every week from business owners looking at {pillar.shortTitle}.</p>
            <div className="mt-8 space-y-6">
              {faqs.map((f, i) => (
                <div
                  key={i}
                  className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6"
                  data-speakable
                  data-testid={`pillar-faq-${i}`}
                >
                  <h3 className="font-display font-semibold text-[#1E2A44] text-lg leading-snug">{f.q}</h3>
                  <p className="text-[#475569] text-[15px] leading-relaxed mt-2 faq-answer">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Final CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">{pillar.finalCtaHeading}</h2>
          <p className="text-white/75 mt-4 text-lg">{pillar.finalCtaSub}</p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix={`pillar-${pillar.slug}-final-cta`} />
          </div>
        </div>
      </Section>
    </main>
  );
}
